/**
 * Dates and times, by named preset, so the same moment reads one way
 * wherever a panel prints it. One clock for the whole app: every moment is
 * printed in UTC, the clock daily figures (NAV, closes) are cut on, never
 * in the reader's own zone. A time of day names it (`zone: true`).
 *
 * Every preset is an `Intl.DateTimeFormat` built on first use and cached.
 */
const PRESETS = {
  /** "May 18" */
  'day-month': { locale: 'en-US', options: { month: 'short', day: 'numeric' } },
  /** "May 18, 2026" */
  'day-month-year': {
    locale: 'en-US',
    options: { month: 'short', day: 'numeric', year: 'numeric' }
  },
  /** "May 2026" */
  'month-year': {
    locale: 'en-US',
    options: { month: 'short', year: 'numeric' }
  },
  /** "May 26" (two digit year, for a crowded axis) */
  'month-short-year': {
    locale: 'en-US',
    options: { month: 'short', year: '2-digit' }
  },
  /** "May 18, 2026, 4:05 PM" */
  'date-time': {
    locale: 'en-US',
    options: {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }
  },
  /** "May 18, 2026, 16:05" */
  'date-time-24h': {
    locale: 'en-US',
    options: {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }
  },
  /** "16:05:09" */
  'time-24h-seconds': {
    locale: 'en-US',
    options: {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }
  },
  /** "04:05 PM" */
  'time-of-day': {
    locale: 'en-US',
    options: { hour: '2-digit', minute: '2-digit' }
  },
  /** "May 18, 2026" (`dateStyle: 'medium'`) */
  'medium-date': { locale: 'en-US', options: { dateStyle: 'medium' } },
  /** "May 18, 2026, 4:05 PM" (`dateStyle: 'medium', timeStyle: 'short'`) */
  'medium-date-time': {
    locale: 'en-US',
    options: { dateStyle: 'medium', timeStyle: 'short' }
  },
  /** "18 May" */
  'day-month-gb': {
    locale: 'en-GB',
    options: { day: 'numeric', month: 'short' }
  },
  /** "18 May, 16:05" */
  'day-month-time-gb': {
    locale: 'en-GB',
    options: {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }
  }
} satisfies Record<
  string,
  { locale: string; options: Intl.DateTimeFormatOptions }
>;

export type DatePreset = keyof typeof PRESETS;

/**
 * The presets written with `dateStyle` or `timeStyle`. `Intl` refuses
 * `timeZoneName` beside those (it throws), so they cannot name the zone.
 */
type StyledPreset = {
  [P in DatePreset]: (typeof PRESETS)[P]['options'] extends
    | { dateStyle: string }
    | { timeStyle: string }
    ? P
    : never;
}[DatePreset];

/**
 * `zone: true` only where the preset is known to take it: a literal preset
 * that is not a `dateStyle` one. A preset known only at run time (a union)
 * cannot be proven to, so it takes no zone either.
 */
interface DateOptions<P extends DatePreset> {
  /** Name the zone after the time ("… UTC"), as `Intl` writes it. */
  zone?: [P] extends [Exclude<DatePreset, StyledPreset>] ? boolean : false;
}

let formatterCache: Map<string, Intl.DateTimeFormat> | undefined;

/** Whether `Intl` can name the zone beside this preset's options. */
function takesZoneName(preset: DatePreset): boolean {
  const { options } = PRESETS[preset];
  return !('dateStyle' in options) && !('timeStyle' in options);
}

function formatterFor(preset: DatePreset, zone: boolean): Intl.DateTimeFormat {
  formatterCache ??= new Map();
  const key = `${preset}|${zone}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    const { locale, options } = PRESETS[preset];
    formatter = new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone: 'UTC',
      ...(zone ? { timeZoneName: 'short' } : {})
    });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/**
 * A moment in one of the presets, or null when the input is not a time, so
 * the caller says what an unreadable time reads as. A string is read by
 * `new Date`; a number is milliseconds since the epoch.
 */
export function formatDate<P extends DatePreset>(
  value: Date | string | number,
  preset: P,
  options: DateOptions<P> = {}
): string | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const zone = options.zone === true;
  // The types keep a zone off the `dateStyle` presets. Should one arrive
  // anyway, the zone is written after the text rather than thrown on.
  if (zone && !takesZoneName(preset)) {
    return `${formatterFor(preset, false).format(date)} UTC`;
  }
  return formatterFor(preset, zone).format(date);
}
